import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiFailure, type GameState } from "./api";
import { Grid } from "./components/Grid";
import { Keyboard } from "./components/Keyboard";
import { ResultSheet } from "./components/ResultSheet";
import { REVEAL_TOTAL_MS } from "./components/Tile";
import { Toast } from "./components/Toast";
import { deriveKeyStates } from "./game/keyboard";
import { colorScheme, getInitData, haptic, initTelegram, tg } from "./telegram";

type Screen =
  | { kind: "loading" }
  | { kind: "error"; message: string; retry: boolean }
  | { kind: "game"; state: GameState };

const ERROR_COPY: Record<string, string> = {
  unauthorized: "Відкрий гру через Telegram — кнопка в чаті або t.me/ch1ps_bot/wordle",
  not_configured: "Сервер ще не налаштований. Скажи чіпсу.",
  no_puzzle: "Немає пазла на сьогодні. Скажи чіпсу, хай підтягне.",
  network: "Нема зв'язку. Спробуй ще раз.",
  server_error: "Щось зламалось. Спробуй ще раз.",
};

export function App() {
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });
  const [current, setCurrent] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [revealRow, setRevealRow] = useState<number | null>(null);
  const [shakeRow, setShakeRow] = useState<number | null>(null);
  const [bounceRow, setBounceRow] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((msg: string, ms = 1400) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  }, []);

  const load = useCallback(async () => {
    setScreen({ kind: "loading" });
    if (!getInitData()) {
      setScreen({ kind: "error", message: ERROR_COPY.unauthorized ?? "", retry: false });
      return;
    }
    try {
      const state = await api.state();
      setScreen({ kind: "game", state });
      if (state.status !== "playing") setSheetOpen(true);
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      setScreen({
        kind: "error",
        message: ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "",
        retry: code !== "unauthorized",
      });
    }
  }, []);

  useEffect(() => {
    initTelegram();
    document.documentElement.dataset.scheme = colorScheme();
    const app = tg();
    const onTheme = () => {
      document.documentElement.dataset.scheme = colorScheme();
    };
    app?.onEvent("themeChanged", onTheme);
    void load();
    return () => app?.offEvent("themeChanged", onTheme);
  }, [load]);

  const state = screen.kind === "game" ? screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const keyStates = useMemo(() => deriveKeyStates(state?.guesses ?? []), [state?.guesses]);

  const submit = useCallback(async () => {
    if (!state || finished || busy) return;
    const row = state.guesses.length;
    if (current.length < 5) {
      setShakeRow(row);
      haptic.error();
      showToast("Замало літер");
      window.setTimeout(() => setShakeRow(null), 650);
      return;
    }
    setBusy(true);
    try {
      const next = await api.guess(current);
      setCurrent("");
      setScreen({ kind: "game", state: next });
      setRevealRow(row);
      haptic.tap();
      if (next.status !== "playing") {
        if (next.status === "won") {
          setBounceRow(row);
          window.setTimeout(() => haptic.success(), REVEAL_TOTAL_MS);
        } else {
          window.setTimeout(() => haptic.error(), REVEAL_TOTAL_MS);
        }
        window.setTimeout(() => setSheetOpen(true), REVEAL_TOTAL_MS + (next.status === "won" ? 900 : 300));
      }
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      if (code === "not_in_dictionary" || code === "invalid_word") {
        setShakeRow(row);
        haptic.error();
        showToast("Нема такого слова");
        window.setTimeout(() => setShakeRow(null), 650);
      } else if (code === "game_over") {
        void load();
      } else {
        showToast(ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "", 2200);
      }
    } finally {
      setBusy(false);
    }
  }, [state, finished, busy, current, showToast, load]);

  const onKey = useCallback(
    (key: string) => {
      if (!state || finished || busy) return;
      if (key === "enter") {
        void submit();
        return;
      }
      if (key === "back") {
        setCurrent((c) => c.slice(0, -1));
        return;
      }
      if (/^[a-z]$/.test(key) && current.length < 5) {
        haptic.tap();
        setCurrent((c) => (c.length < 5 ? c + key : c));
      }
    },
    [state, finished, busy, current.length, submit],
  );

  // Physical keyboard (desktop Telegram / browser).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Enter") onKey("enter");
      else if (e.key === "Backspace") onKey("back");
      else if (/^[a-zA-Z]$/.test(e.key)) onKey(e.key.toLowerCase());
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onKey]);

  if (screen.kind === "loading") {
    return (
      <Shell>
        <div className="flex flex-1 items-center justify-center text-tghint">…</div>
      </Shell>
    );
  }

  if (screen.kind === "error") {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <div className="text-base">{screen.message}</div>
          {screen.retry && (
            <button
              type="button"
              onClick={() => void load()}
              className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext"
            >
              ще раз
            </button>
          )}
        </div>
      </Shell>
    );
  }

  const s = screen.state;
  return (
    <Shell
      header={
        <header className="flex items-center justify-between px-4 pb-2 pt-[calc(env(safe-area-inset-top)+10px)]">
          <div className="text-xs text-tghint">
            #{s.number.toLocaleString("en-US")}
          </div>
          <div className="text-lg font-extrabold tracking-[0.2em]">WORDLE</div>
          <button
            type="button"
            onClick={() => finished && setSheetOpen(true)}
            className={`text-xs ${finished ? "text-tgtext" : "text-tghint"}`}
          >
            🔥 {s.me.streak}
          </button>
        </header>
      }
    >
      <div className="flex flex-1 items-center justify-center py-1">
        <Grid
          guesses={s.guesses}
          current={current}
          revealRow={revealRow}
          shakeRow={shakeRow}
          bounceRow={bounceRow}
          finished={finished}
        />
      </div>
      <div
        style={{
          paddingBottom:
            "max(env(safe-area-inset-bottom, 0px), var(--tg-safe-area-inset-bottom, 0px), var(--tg-content-safe-area-inset-bottom, 0px), 10px)",
        }}
      >
        <div className="mx-auto mb-[10px] w-full max-w-[520px] px-[6px]">
          <button
            type="button"
            disabled={finished || busy || current.length < 5}
            onPointerDown={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="key flex h-[50px] w-full items-center justify-center rounded-[10px] bg-tgbutton text-[16px] font-bold uppercase tracking-wide text-tgbuttontext transition-all duration-100 active:scale-[0.98] active:brightness-90 disabled:opacity-35"
          >
            {busy ? "…" : "Перевірити"}
          </button>
        </div>
        <Keyboard states={keyStates} onKey={onKey} disabled={busy || finished} />
      </div>
      <Toast message={toast} />
      {sheetOpen && finished && <ResultSheet state={s} onDismiss={() => setSheetOpen(false)} />}
    </Shell>
  );
}

function Shell({ header, children }: { header?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[560px] flex-col bg-tgbg text-tgtext"
      style={{ height: "var(--tg-viewport-stable-height, 100vh)", minHeight: "100%" }}
    >
      {header}
      {children}
    </div>
  );
}
